import { Link, useNavigate } from 'react-router-dom'
import AuthShell from '../../ev-journey/components/AuthShell'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import z from 'zod'
import client from '@/http/customer-client'
import { restApiErrorHandler } from '@/lib/restApiErrorHandler'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/customer-ui/input'
import { Button } from '@/components/customer-ui/button'
import i18n from '@/i18n'
import { Loader2 } from 'lucide-react'

const formSchema = z.object({
  email: z
    .email({ message: i18n.t('page_login_customer.validation_invalid_email') })
    .min(1, { message: i18n.t('validation.email.required') })
})

async function apiForgotPassword(email: string) {
  return (
    await client.post<{ token: string }>('/app-api/forgot-password', {
      email
    })
  ).data
}

export default function ForgotPasswordPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const forgotPasswordMutation = useMutation({
    mutationFn: async (credentials: z.infer<typeof formSchema>) =>
      await apiForgotPassword(credentials.email),
    onError: restApiErrorHandler,
    onSuccess: () => {
      void navigate('/customer/login')
      toast.success(t('page_forgot_password.toast_title'))
    }
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ''
    }
  })

  function onSubmit(formData: z.infer<typeof formSchema>) {
    forgotPasswordMutation.mutate(formData)
  }
  return (
    <AuthShell
      title='Wachtwoord vergeten'
      subtitle='Voer je e-mailadres in om je wachtwoord opnieuw in te stellen.'
    >
      <Form {...form}>
        <form onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}>
          <div className='grid gap-2'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='space-y-1'>
                  <FormLabel>{t('page_forgot_password.email_label')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('page_forgot_password.email_placeholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              className='mt-2'
              disabled={forgotPasswordMutation.isPending}
            >
              {forgotPasswordMutation.isPending && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              <span>{t('common.submit')}</span>
            </Button>
            <Link
              to='/customer/login'
              className='flex items-center justify-center gap-1 text-sm underline decoration-solid hover:opacity-75'
            >
              {t('button.back_to_log_in')}
            </Link>
          </div>
        </form>
      </Form>
    </AuthShell>
  )
}
